import { accessAreaBranches } from "@/constants/area-constants";
import { TreeHierarchy } from "../components/checkbox-tree/checkboxNodeField";
import { AreaLocation } from "../models/AreaLocation";
import { Door } from "../models/Door";
import { Zone } from "../models/Zone";

const buildDoors = (doors: Door[], zoneId, areaLocation: AreaLocation[], status?: boolean): TreeHierarchy[] => {

    return doors.map((door): TreeHierarchy => {
        const areaLocationData = areaLocation?.find(location => location.doorId === door.id);

        return {
            name: door.name,
            label: door.name,
            options: [],
            id: door.id.toString(),
            parentId: zoneId,
            checked: !!areaLocationData,
            areaLocationId: areaLocationData?.id.toString(),
            branch: accessAreaBranches.LOCATION,
        };
    });
};

const buildZones = (zones: Zone[], parentId, areId): TreeHierarchy[] => {

    return zones.map((zone): TreeHierarchy => {
        let checked = false;

        if (zone.areaLocationCounts && zone.areaLocationCounts.length > 0) {

            const areaCounts = zone.areaLocationCounts.find(areaLocationcount => areaLocationcount['area-id'].toString() == areId);

            if (areaCounts) {
                checked = areaCounts.count > 0;
            }
        }

        const zoneBuild: TreeHierarchy = {
            name: zone.name,
            id: zone.id.toString(),
            label: zone.name,
            options: [],
            checked: checked,
            parentId: parentId,
            branch: accessAreaBranches.ZONE
        }
        return zoneBuild;
    });
}
    
const buildBasicControllers = (controllers, siteId) =>
    controllers.map((controller) => {
        return {
            name: `site/${siteId}/controller/${controller.id}`,
            id: controller.id,
            label: controller.name,
            options: [],
            parentId: siteId,
            siteId: siteId,
            controllerId: controller.id,
            branch: accessAreaBranches.CONTROLLER,
            value: false,
        };
    });

/**
 * Toggle the controller value and update the zones and doors under the controller.
 *
 * @param {object} node - The node representing the controller.
 * @param {array} areaBranch - The state of the door tree.
 */
const toggleController = (areaBranch: TreeHierarchy | undefined, controllerId) => {

    let isChecked = false;

    // Find and toggle the specific controller and its zones and doors
    if (areaBranch) {
        areaBranch.options = areaBranch.options.map(controller => {
            // select correct controller && check if controller has zones 
            if (controller.id === controllerId) {
                // Toggle the controller's checked state
                controller.checked = !controller.checked;
                isChecked = controller.checked;
                if (controller?.options?.length > 0) {
                    // Propagate the change to all zones and doors under this controller
                    controller.options = controller.options.map(zone => {
                        zone.checked = controller.checked; // Set the zone's value to match the controller's new value

                        // Do the same for doors under each zone
                        zone.options = zone.options.map(door => {
                            door.checked = controller.checked; // Set the door's value to match the controller's new value
                            return door; // Return the modified door
                        });

                        return zone; // Return the modified zone
                    });
                }
            }
            return controller; // Return the modified controller (or unmodified if not the one we're looking for)
        });

        areaBranch.checked = areaBranch.options.some(controller => controller.checked);
    }
    return { currentBranch: areaBranch, isChecked }; // Return the unmodified areaBranch
}

const toggleZone = (areaBranch: TreeHierarchy | undefined, controllerId, zoneId) => {

    let isChecked = false;
    if (areaBranch) {
        areaBranch.options = areaBranch.options.map(controller => {
            if (controller.id === controllerId) {
                controller.options = controller.options.map(zone => {
                    if (zone.id === zoneId) {
                        // Toggle the zone's checked state and all doors within it
                        zone.checked = !zone.checked;
                        isChecked = zone.checked;
                        zone.options = zone.options.map(door => {
                            door.checked = zone.checked; // Match the door's value to the zone's new value
                            return door;
                        });

                    }
                    return zone;
                });

                controller.checked = controller.options.some(zone => zone.checked);
            }
            return controller;
        });
        areaBranch.checked = areaBranch.options.some(controller => controller.checked);
    }
    return { currentBranch: areaBranch, isChecked };
};


/**
 * Toggle the door checked property
 *
 * @param {object} node - The node representing the door.
 * @param {array} areaBranch - The state of the door tree.
 */
const toggleDoor = (areaBranch: TreeHierarchy | undefined, controllerId, zoneId, doorId) => {

    let isChecked = false;
    const toggledIndexes = { // keeps track of the node you're toggling
        controllerIndex: -1,
        zoneIndex: -1,
        doorIndex: -1
    };

    if (areaBranch) {
        areaBranch.options = areaBranch.options.map((controller, controllerIndex) => {
            if (controller.id === controllerId) {
                controller.options = controller.options.map((zone, zoneIndex) => {
                    if (zone.id === zoneId) {
                        zone.options = zone.options.map((door, doorIndex) => {
                            if (door.id === doorId) {
                                door.checked = !door.checked;
                                isChecked = door.checked;
                                toggledIndexes.controllerIndex = controllerIndex;
                                toggledIndexes.zoneIndex = zoneIndex;
                                toggledIndexes.doorIndex = doorIndex;
                            }
                            return door;
                        });
                        zone.checked = zone.options.some(door => door.checked);
                    }

                    return zone;
                });
                controller.checked = controller.options.some(zone => zone.checked);
            }

            return controller;
        });
        areaBranch.checked = areaBranch.options.some(controller => controller.options.some(zone => zone.options.some(door => door.checked)));
    }

    return { currentBranch: areaBranch, isChecked, toggledIndexes };
};


export {
    buildBasicControllers,
    buildDoors,
    buildZones,
    toggleController, toggleDoor, toggleZone
};

